import React, { useState, useEffect } from 'react'
import {
  Box,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Avatar,
  Menu,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
} from '@mui/material'
import {
  Search,
  Filter,
  Eye,
  Edit,
  MoreVertical,
  Package,
  CheckCircle,
  XCircle,
  Clock,
  Send,
  X,
} from 'lucide-react'
import API from '../../utils/API'
import { useAuth } from '../../utils/authContext'

const OrderStatusBadge = ({ status }) => {
  const statusConfig = {
    pending: { 
      color: 'warning', 
      label: 'Pending', 
      icon: Clock,
      bg: 'linear-gradient(135deg, #FFBB33 0%, #FFC107 100%)',
      textColor: 'white'
    },
    confirmed: { 
      color: 'primary', 
      label: 'Confirmed', 
      icon: CheckCircle,
      bg: 'linear-gradient(135deg, #9C27B0 0%, #BA68C8 100%)',
      textColor: 'white'
    },
    completed: { 
      color: 'success', 
      label: 'Completed', 
      icon: CheckCircle,
      bg: 'linear-gradient(135deg, #00C851 0%, #4CAF50 100%)',
      textColor: 'white'
    },
    cancelled: { 
      color: 'error', 
      label: 'Cancelled', 
      icon: XCircle,
      bg: 'linear-gradient(135deg, #FF4444 0%, #F44336 100%)',
      textColor: 'white'
    },
    refunded: { 
      color: 'default', 
      label: 'Refunded', 
      icon: XCircle,
      bg: 'linear-gradient(135deg, #718096 0%, #A0AEC0 100%)',
      textColor: 'white'
    },
  }
  
  const config = statusConfig[status] || statusConfig.pending
  const Icon = config.icon
  
  return (
    <Chip
      icon={<Icon size={12} />}
      label={config.label}
      size="small"
      sx={{
        background: config.bg,
        color: config.textColor,
        fontWeight: 600,
        border: 'none',
        '& .MuiChip-icon': {
          color: config.textColor,
        },
      }}
    />
  )
}

const OrderRow = ({ order, onView, onEdit, onUpdateStatus, onResendTickets }) => {
      const [anchorEl, setAnchorEl] = useState(null)
      const open = Boolean(anchorEl)
      
      const handleClick = (event) => {
        setAnchorEl(event.currentTarget)
      }
      
      const handleClose = () => {
        setAnchorEl(null)
      }
      
      const formatDate = (dateString) => {
        if (!dateString) return 'N/A'
        const date = new Date(dateString)
        return date.toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'short', 
          day: 'numeric' 
        })
      }
      
      return (
        <TableRow sx={{ '&:hover': { backgroundColor: 'grey.50' } }}>
          <TableCell>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem' }}>
                {order.customer_name?.split(' ').map(n => n[0]).join('') || 'C'}
              </Avatar>
              <Box>
                <Typography variant="body2" fontWeight="semibold">{order.customer_name || 'Unknown Customer'}</Typography>
                <Typography variant="caption" color="grey.600">
                  {order.customer_email || 'No email'}
                </Typography>
              </Box>
            </Box>
          </TableCell>
          <TableCell>
            <Box>
              <Typography variant="body2" fontWeight="medium">{order.show_name || order.show_title || 'Unknown Event'}</Typography>
              <Typography variant="caption" color="grey.600">
                {formatDate(order.show_date)}
              </Typography>
            </Box>
          </TableCell>
          <TableCell>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Package size={16} color="gray" />
              <Typography variant="body2">{order.ticket_quantity || 0}</Typography>
            </Box>
          </TableCell>
          <TableCell>
            <Typography variant="body2" fontWeight="semibold" color="success.main">
              ${order.total_amount?.toFixed(2) || '0.00'}
            </Typography>
          </TableCell>
          <TableCell>
            <OrderStatusBadge status={order.status || 'pending'} />
          </TableCell>
          <TableCell>
            <Typography variant="body2" color="grey.600">
              {formatDate(order.created_at)}
            </Typography>
          </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="View Order">
            <IconButton
              size="small"
              onClick={() => onView(order)}
              aria-label="View order"
            >
              <Eye size={16} />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Resend Tickets">
            <IconButton
              size="small"
              onClick={() => onResendTickets(order.id)}
              aria-label="Resend tickets"
              color="primary"
            >
              <Send size={16} />
            </IconButton>
          </Tooltip>
          
          {/* Actions Menu */}
          <Tooltip title="More actions">
            <IconButton
              size="small"
              onClick={handleClick}
              aria-label="Actions"
            >
              <MoreVertical size={16} />
            </IconButton>
          </Tooltip>
          
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            PaperProps={{
              sx: { mt: 1, minWidth: 200 },
            }}
          >
            <ListItemButton onClick={() => { onView(order); handleClose(); }}>
              <ListItemIcon>
                <Eye size={16} />
              </ListItemIcon>
              <ListItemText>View Details</ListItemText>
            </ListItemButton>
            <ListItemButton onClick={() => { onResendTickets(order.id); handleClose(); }}>
              <ListItemIcon>
                <Send size={16} />
              </ListItemIcon>
              <ListItemText>Resend Tickets</ListItemText>
            </ListItemButton>
            <Divider />
            <ListItemButton onClick={() => { onUpdateStatus(order.id, 'confirmed'); handleClose(); }}>
              <ListItemIcon>
                <Package size={16} />
              </ListItemIcon>
              <ListItemText>Confirm</ListItemText>
            </ListItemButton>
            <ListItemButton onClick={() => { onUpdateStatus(order.id, 'completed'); handleClose(); }}>
              <ListItemIcon>
                <CheckCircle size={16} />
              </ListItemIcon>
              <ListItemText>Complete</ListItemText>
            </ListItemButton>
          </Menu>
        </Box>
      </TableCell>
    </TableRow>
  )
}

export default function Orders() {
      const { showrunner, loading: authLoading } = useAuth()
      const [searchTerm, setSearchTerm] = useState('')
      const [statusFilter, setStatusFilter] = useState('all')
      const [eventFilter, setEventFilter] = useState('all')
      const [loading, setLoading] = useState(true)
      const [orders, setOrders] = useState([])
      const [events, setEvents] = useState([])
      const [viewModalOpen, setViewModalOpen] = useState(false)
      const [selectedOrder, setSelectedOrder] = useState(null)
      const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' })
      
      useEffect(() => {
        if (authLoading) return
        if (!showrunner?.id) {
          setLoading(false)
          return
        }
        fetchOrdersData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [showrunner, authLoading])
      
      const fetchOrdersData = async () => {
        if (!showrunner?.id) {
          setLoading(false)
          return
        }
        
        try {
          setLoading(true)
          
          const showrunnerId = showrunner.id
          
          // Fetch recent orders
          const ordersResponse = await API.getRecentOrders(showrunnerId, 50, 30)
          
          // Map API response to component's expected format
          const mappedOrders = ordersResponse.data.map(order => ({
            ...order,
            customer_name: order.full_name,
            customer_email: order.email,
            show_name: order.show,
            show_title: order.show,
            ticket_quantity: order.tickets_sold,
            total_amount: order.total,
            created_at: order.date_created,
            status: order.refund ? 'refunded' : 'completed',
            show_date: order.date_created
          }))
          
          setOrders(mappedOrders)
          
          // Fetch shows for event filter
          const eventsResponse = await API.getShows(`?showrunner_id=${showrunnerId}`)
          setEvents(eventsResponse.data)
        } catch (error) {
          console.error('Error fetching orders data:', error)
        } finally {
          setLoading(false)
        }
      }
  
      const filteredOrders = orders.filter(order => {
        const matchesSearch = order.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             order.customer_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             (order.show_name || order.show_title)?.toLowerCase().includes(searchTerm.toLowerCase())
        const matchesStatus = statusFilter === 'all' || order.status === statusFilter
        const matchesEvent = eventFilter === 'all' || (order.show_name || order.show_title) === eventFilter
        
        return matchesSearch && matchesStatus && matchesEvent
      })
      
      const handleViewOrder = (order) => {
        setSelectedOrder(order)
        setViewModalOpen(true)
      }
      
      const handleEditOrder = (order) => {
        console.log('Edit order:', order)
        // Navigate to edit order page
      }
      
      const handleUpdateStatus = async (orderId, newStatus) => {
        try {
          // Update order status via API
          await API.confirmOrderDelivery({ order_id: orderId, status: newStatus })
          await fetchOrdersData() // Refresh the list
          setSnackbar({ open: true, message: 'Order status updated successfully', severity: 'success' })
        } catch (error) {
          console.error('Error updating order status:', error)
          setSnackbar({ open: true, message: 'Failed to update order status', severity: 'error' })
        }
      }
      
      const handleResendTickets = async (orderId) => {
        try {
          setSnackbar({ open: true, message: 'Resending tickets...', severity: 'info' })
          const response = await API.resendTix({ orderID: orderId })
          if (response.data.error) {
            setSnackbar({ open: true, message: 'There was an issue resending the tickets. Please try again later.', severity: 'error' })
          } else {
            setSnackbar({ open: true, message: 'Tickets resent successfully', severity: 'success' })
          }
        } catch (error) {
          console.error('Error resending tickets:', error)
          setSnackbar({ open: true, message: 'There was an issue resending the tickets. Please try again later.', severity: 'error' })
        }
      }
      
      const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false })
      }
      
      const formatDate = (dateString) => {
        if (!dateString) return 'N/A'
        const date = new Date(dateString)
        return date.toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      }
      
      if (loading) {
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress size={60} />
          </Box>
        )
      }
  
      const getOrderStats = () => {
        const total = orders.length
        const pending = orders.filter(o => o.status === 'pending').length
        const confirmed = orders.filter(o => o.status === 'confirmed').length
        const completed = orders.filter(o => o.status === 'completed').length
        
        return { total, pending, confirmed, completed }
      }
  
  const stats = getOrderStats()
  
  return (
    <Box>
      {/* Header */}
      <Box sx={{ 
        mb: 3,
        p: 3,
        background: 'linear-gradient(135deg, #E8F0FF 0%, #F0E8FF 100%)',
        borderRadius: 3,
        border: '2px solid',
        borderColor: 'primary.light',
        boxShadow: '0 4px 12px rgba(0, 41, 255, 0.1)',
      }}>
        <Typography variant="h4" fontWeight="bold" sx={{ mb: 1, color: 'primary.main' }}>
          📦 Orders
        </Typography>
        <Typography color="grey.700" fontWeight="500">
          Manage customer orders and ticket sales
        </Typography>
      </Box>
      
      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, #E8F0FF 0%, #ffffff 100%)',
            border: '2px solid #4A5BFF',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 25px rgba(74, 91, 255, 0.2)',
            },
            transition: 'all 0.3s ease-in-out',
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <Avatar sx={{
                  background: 'linear-gradient(135deg, #0029FF 0%, #4A5BFF 100%)',
                  width: 56,
                  height: 56,
                  mb: 1,
                  boxShadow: '0 4px 12px rgba(0, 41, 255, 0.3)',
                }}>
                  <Package size={24} />
                </Avatar>
                <Typography color="grey.600" variant="body2" fontWeight="600">Total Orders</Typography>
                <Typography variant="h4" fontWeight="bold" color="primary.main">{stats.total}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, #FFF8E0 0%, #ffffff 100%)',
            border: '2px solid #FFBB33',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 25px rgba(255, 187, 51, 0.2)',
            },
            transition: 'all 0.3s ease-in-out',
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <Avatar sx={{
                  background: 'linear-gradient(135deg, #FFBB33 0%, #FFC107 100%)',
                  width: 56,
                  height: 56,
                  mb: 1,
                  boxShadow: '0 4px 12px rgba(255, 187, 51, 0.3)',
                }}>
                  <Clock size={24} />
                </Avatar>
                <Typography color="grey.600" variant="body2" fontWeight="600">Pending</Typography>
                <Typography variant="h4" fontWeight="bold" color="warning.main">{stats.pending}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, #F0E8FF 0%, #ffffff 100%)',
            border: '2px solid #9C27B0',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 25px rgba(156, 39, 176, 0.2)',
            },
            transition: 'all 0.3s ease-in-out',
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <Avatar sx={{
                  background: 'linear-gradient(135deg, #9C27B0 0%, #BA68C8 100%)',
                  width: 56,
                  height: 56,
                  mb: 1,
                  boxShadow: '0 4px 12px rgba(156, 39, 176, 0.3)',
                }}>
                  <CheckCircle size={24} />
                </Avatar>
                <Typography color="grey.600" variant="body2" fontWeight="600">Confirmed</Typography>
                <Typography variant="h4" fontWeight="bold" sx={{ color: '#9C27B0' }}>{stats.confirmed}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, #E8F5E8 0%, #ffffff 100%)',
            border: '2px solid #00C851',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 8px 25px rgba(0, 200, 81, 0.2)',
            },
            transition: 'all 0.3s ease-in-out',
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <Avatar sx={{
                  background: 'linear-gradient(135deg, #00C851 0%, #4CAF50 100%)',
                  width: 56,
                  height: 56,
                  mb: 1,
                  boxShadow: '0 4px 12px rgba(0, 200, 81, 0.3)',
                }}>
                  <CheckCircle size={24} />
                </Avatar>
                <Typography color="grey.600" variant="body2" fontWeight="600">Completed</Typography>
                <Typography variant="h4" fontWeight="bold" color="success.main">{stats.completed}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              placeholder="Search orders..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{ maxWidth: '300px' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={16} color="gray" />
                  </InputAdornment>
                ),
              }}
            />
            
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="confirmed">Confirmed</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
                <MenuItem value="refunded">Refunded</MenuItem>
              </Select>
            </FormControl>
            
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Event</InputLabel>
                  <Select
                    value={eventFilter}
                    onChange={(e) => setEventFilter(e.target.value)}
                    label="Event"
                  >
                    <MenuItem value="all">All Events</MenuItem>
                    {events.map(event => (
                      <MenuItem key={event.id} value={event.production?.name || event.name || event.title}>
                        {event.production?.name || event.name || event.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
            
            <Button
              variant="contained"
              size="small"
              startIcon={<Filter size={16} />}
              sx={{
                background: 'linear-gradient(135deg, #FF6B35 0%, #FF8A65 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #E65100 0%, #FF6B35 100%)',
                },
              }}
            >
              More Filters
            </Button>
          </Box>
        </CardContent>
      </Card>
      
      {/* Orders Table */}
      <Card>
        <CardContent sx={{ padding: 0 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Customer</TableCell>
                <TableCell>Event</TableCell>
                <TableCell>Tickets</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Order Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredOrders.map((order) => (
                <OrderRow
                  key={order.id}
                  order={order}
                  onView={handleViewOrder}
                  onEdit={handleEditOrder}
                  onUpdateStatus={handleUpdateStatus}
                  onResendTickets={handleResendTickets}
                />
              ))}
            </TableBody>
          </Table>
          
          {filteredOrders.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography color="grey.500">No orders found</Typography>
            </Box>
          )}
        </CardContent>
      </Card>
      
      {/* View Order Modal */}
      <Dialog 
        open={viewModalOpen} 
        onClose={() => setViewModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight="bold">Order Details</Typography>
          <IconButton onClick={() => setViewModalOpen(false)} size="small">
            <X size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedOrder && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Customer Information */}
              <Box>
                <Typography variant="subtitle2" color="grey.600" gutterBottom>
                  Customer Information
                </Typography>
                <Card variant="outlined">
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="grey.600">Name</Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {selectedOrder.customer_name || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="grey.600">Email</Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {selectedOrder.customer_email || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="grey.600">Phone</Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {selectedOrder.phone_number || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="grey.600">Order ID</Typography>
                        <Typography variant="body1" fontWeight="medium">
                          #{selectedOrder.id}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Box>
              
              {/* Order Information */}
              <Box>
                <Typography variant="subtitle2" color="grey.600" gutterBottom>
                  Order Information
                </Typography>
                <Card variant="outlined">
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="grey.600">Event</Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {selectedOrder.show_name || selectedOrder.show_title || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="grey.600">Order Date</Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {formatDate(selectedOrder.created_at)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="grey.600">Confirmation Code</Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {selectedOrder.confirmation_code || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="grey.600">Status</Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <OrderStatusBadge status={selectedOrder.status || 'pending'} />
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="grey.600">Number of Tickets</Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {selectedOrder.ticket_quantity || 0}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="grey.600">Payment Method</Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {selectedOrder.method || 'N/A'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Box>
              
              {/* Ticket Details */}
              {selectedOrder.tickets && (
                <Box>
                  <Typography variant="subtitle2" color="grey.600" gutterBottom>
                    Ticket Seats
                  </Typography>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="body2">
                        {selectedOrder.tickets}
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
              )}
              
              {/* Pricing Information */}
              <Box>
                <Typography variant="subtitle2" color="grey.600" gutterBottom>
                  Pricing
                </Typography>
                <Card variant="outlined">
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" color="grey.600">Subtotal</Typography>
                          <Typography variant="body2" fontWeight="medium">
                            ${(selectedOrder.total_amount || 0).toFixed(2)}
                          </Typography>
                        </Box>
                        {selectedOrder.st_fee_total > 0 && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="grey.600">Service Fee</Typography>
                            <Typography variant="body2" fontWeight="medium">
                              ${(selectedOrder.st_fee_total || 0).toFixed(2)}
                            </Typography>
                          </Box>
                        )}
                        {selectedOrder.cc_fee > 0 && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="grey.600">Processing Fee</Typography>
                            <Typography variant="body2" fontWeight="medium">
                              ${(selectedOrder.cc_fee || 0).toFixed(2)}
                            </Typography>
                          </Box>
                        )}
                        <Divider sx={{ my: 1.5 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body1" fontWeight="bold">Total</Typography>
                          <Typography variant="body1" fontWeight="bold" color="success.main">
                            ${(selectedOrder.order_total || selectedOrder.total_amount || 0).toFixed(2)}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Box>
              
              {selectedOrder.gratis && (
                <Alert severity="info">
                  This is a complimentary (gratis) order
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleResendTickets(selectedOrder?.id)} startIcon={<Send size={16} />}>
            Resend Tickets
          </Button>
          <Button onClick={() => setViewModalOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}
